import React, { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/authService';
import toast from 'react-hot-toast';

// type: 'contract' (default), 'delivery', 'return'
const SignaturesModal = ({ isOpen, onClose, contractId, type = 'contract' }) => {
  const [signatures, setSignatures] = useState([]);
  const [contractData, setContractData] = useState(null);
  const [termDocumentKey, setTermDocumentKey] = useState(null);
  const [stats, setStats] = useState({ total: 0, signed: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && contractId) {
      fetchData();
    }
  }, [isOpen, contractId]);

  // Auto-refresh a cada 30s enquanto modal estiver aberto e houver pendências
  useEffect(() => {
    if (!isOpen || !contractId) return;
    const interval = setInterval(() => {
      if (stats.pending > 0) fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [isOpen, contractId, stats.pending]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let sigData;
      if (type === 'delivery' || type === 'return') {
        const res = await api.get(`/api/contracts/${contractId}/term-signatures?type=${type}`);
        sigData = res.data;
        const firstSig = (sigData.signatures || [])[0];
        if (firstSig?.clicksign_document_key) {
          setTermDocumentKey(firstSig.clicksign_document_key);
        }
      } else {
        const res = await api.get(`/api/contracts/${contractId}/signatures`);
        sigData = res.data;
        setTermDocumentKey(null);
      }
      setSignatures(sigData.signatures || []);
      setStats({
        total: sigData.total || 0,
        signed: sigData.signed || 0,
        pending: sigData.pending || 0
      });

      const contractResponse = await api.get(`/api/contracts/${contractId}`);
      setContractData(contractResponse.data.contract);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar informações');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (signature) => {
    const docKey = (type !== 'contract' && termDocumentKey) ? termDocumentKey : contractData?.clicksign_document_key;
    if (!docKey) {
      toast.error('Documento não encontrado no Clicksign');
      return;
    }

    setResendingEmail({ ...resendingEmail, [signature.id]: true });

    try {
      await api.post(`/api/clicksign/documents/${docKey}/resend-email`, {
        email: signature.signer_email,
        signerKey: signature.signer_key,
        type: type !== 'contract' ? type : undefined
      });

      toast.success(`Email reenviado para ${signature.signer_name}`);
    } catch (error) {
      console.error('Erro ao reenviar email:', error);
      toast.error(error.response?.data?.error || 'Erro ao reenviar email');
    } finally {
      setResendingEmail({ ...resendingEmail, [signature.id]: false });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post(`/api/clicksign/contracts/${contractId}/sync-signatures`);

      if (response.data.success) {
        toast.success(response.data.message || 'Assinaturas sincronizadas com sucesso!');

        // Recarregar dados após sincronização
        await fetchData();

        // Se todas assinaturas foram concluídas, informar ao usuário
        if (response.data.status?.allSigned) {
          toast.success('🎉 Todas as assinaturas foram concluídas!');
        }
      }
    } catch (error) {
      console.error('Erro ao sincronizar assinaturas:', error);
      toast.error(error.response?.data?.error || 'Erro ao sincronizar assinaturas');
    } finally {
      setSyncing(false);
    }
  };

  const handleStartEdit = (signature) => {
    setEditingId(signature.id);
    setEditEmail(signature.signer_email);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const handleSaveEmail = async (signatureId) => {
    if (!editEmail || !editEmail.includes('@')) {
      toast.error('Email inválido');
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/api/clicksign/signatures/${signatureId}`, { email: editEmail });
      toast.success('Email atualizado no Clicksign');
      setEditingId(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar email');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditKeyDown = (e, signatureId) => {
    if (e.key === 'Enter') handleSaveEmail(signatureId);
    if (e.key === 'Escape') setEditingId(null);
  };

  const getRoleBadge = (role) => {
    // Locador
    if (role === 'locador') {
      return { text: 'LOCADOR', class: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' };
    }

    // Locatários (dinâmico: locatario, locatario2, locatario3, etc)
    if (role && role.startsWith('locatario')) {
      const number = role === 'locatario' ? '1' : role.replace('locatario', '');
      return { text: `LOCATÁRIO ${number}`, class: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' };
    }

    // Testemunhas
    if (role === 'testemunha1') {
      return { text: 'TESTEMUNHA 1', class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' };
    }
    if (role === 'testemunha2') {
      return { text: 'TESTEMUNHA 2', class: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' };
    }

    // Default
    return { text: role?.toUpperCase() || 'SIGNATÁRIO', class: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300' };
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;

    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let colorClass = 'text-gray-700 dark:text-gray-300';
    let icon = '📅';

    if (diffDays < 0) {
      colorClass = 'text-red-600 dark:text-red-400';
      icon = '⚠️';
    } else if (diffDays <= 2) {
      colorClass = 'text-orange-600 dark:text-orange-400';
      icon = '⏰';
    } else if (diffDays <= 7) {
      colorClass = 'text-yellow-600 dark:text-yellow-400';
      icon = '🕐';
    }

    return {
      date: formatDate(deadline),
      days: diffDays,
      colorClass,
      icon
    };
  };

  if (!isOpen) return null;

  const deadline = contractData?.clicksign_deadline ? formatDeadline(contractData.clicksign_deadline) : null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">
                  📝 {type === 'delivery' ? 'Assinaturas — Termo de Entrega' : type === 'return' ? 'Assinaturas — Termo de Devolução' : 'Status das Assinaturas'}
                </h3>
                <p className="text-sm text-purple-100 mt-1">
                  Acompanhe em tempo real o progresso das assinaturas digitais
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSync}
                  disabled={syncing || loading}
                  className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sincronizar assinaturas do Clicksign"
                >
                  <ArrowPathIcon className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                  <span className="text-sm font-medium">
                    {syncing ? 'Sincronizando...' : 'Sincronizar'}
                  </span>
                </button>
                <button
                  onClick={onClose}
                  className="rounded-md text-purple-200 hover:text-white focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Deadline Alert (se existir e estiver próximo) */}
          {deadline && deadline.days <= 7 && (
            <div className={`px-6 py-3 border-b ${
              deadline.days < 0
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : deadline.days <= 2
                ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-center gap-2">
                <CalendarIcon className={`w-5 h-5 ${deadline.colorClass}`} />
                <span className={`text-sm font-medium ${deadline.colorClass}`}>
                  {deadline.icon} {deadline.days < 0
                    ? `Prazo vencido há ${Math.abs(deadline.days)} dias!`
                    : deadline.days === 0
                    ? 'Prazo vence HOJE!'
                    : deadline.days === 1
                    ? 'Prazo vence AMANHÃ!'
                    : `Faltam ${deadline.days} dias para o prazo`
                  } ({deadline.date})
                </span>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.total}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Total de Signatários
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {stats.signed}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  ✅ Assinadas
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.pending}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  ⏳ Pendentes
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-700 dark:text-gray-300 font-medium">Progresso Geral</span>
                <span className="font-bold text-purple-600 dark:text-purple-400">
                  {stats.total > 0 ? Math.round((stats.signed / stats.total) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-600 to-purple-500 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: `${stats.total > 0 ? (stats.signed / stats.total) * 100 : 0}%` }}
                >
                  {stats.signed > 0 && (
                    <span className="text-xs font-bold text-white">{stats.signed}/{stats.total}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
                <p className="text-gray-500 dark:text-gray-400 mt-2">Carregando informações...</p>
              </div>
            ) : signatures.length === 0 ? (
              <div className="text-center py-8">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  Nenhum signatário cadastrado
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Envie o contrato para o Clicksign primeiro
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {signatures.map((signature, index) => {
                  const roleBadge = getRoleBadge(signature.signer_role);

                  return (
                    <div
                      key={signature.id}
                      className={`
                        border-2 rounded-lg p-4 transition-all
                        ${signature.signed
                          ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                          : 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10'
                        }
                      `}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          {/* Badge e Status */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${roleBadge.class}`}>
                              {roleBadge.text}
                            </span>
                            {signature.signed ? (
                              <div className="flex items-center gap-1">
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                <span className="text-xs font-semibold text-green-700 dark:text-green-400">
                                  Assinado
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <ClockIcon className="h-5 w-5 text-orange-500" />
                                <span className="text-xs font-semibold text-orange-700 dark:text-orange-400">
                                  Aguardando
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Nome e Email */}
                          <p className="font-bold text-gray-900 dark:text-white text-lg">
                            {signature.signer_name}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                            <EnvelopeIcon className="w-4 h-4 flex-shrink-0" />
                            {editingId === signature.id ? (
                              <div className="flex items-center gap-1 flex-1">
                                <input
                                  ref={editInputRef}
                                  type="email"
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                  onKeyDown={(e) => handleEditKeyDown(e, signature.id)}
                                  onBlur={() => { if (!savingEdit) setEditingId(null); }}
                                  className="flex-1 px-2 py-1 text-sm border border-purple-400 dark:border-purple-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:outline-none"
                                  disabled={savingEdit}
                                />
                                <button
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleSaveEmail(signature.id)}
                                  disabled={savingEdit}
                                  className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-medium"
                                >
                                  {savingEdit ? '...' : 'Salvar'}
                                </button>
                              </div>
                            ) : (
                              <span className="flex items-center gap-1">
                                {signature.signer_email}
                                {!signature.signed && (
                                  <button
                                    onClick={() => handleStartEdit(signature)}
                                    className="p-0.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                                    title="Alterar email do signatário"
                                  >
                                    <PencilSquareIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </span>
                            )}
                          </div>

                          {/* Data de Assinatura */}
                          {signature.signed && signature.signed_at && (
                            <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-200 dark:border-green-800">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-green-700 dark:text-green-400" />
                                <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                                  Assinado em {formatDate(signature.signed_at)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Botão Reenviar Email (apenas para pendentes) */}
                        {!signature.signed && (
                          <button
                            onClick={() => handleResendEmail(signature)}
                            disabled={resendingEmail[signature.id]}
                            className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                            title="Reenviar email de assinatura"
                          >
                            <ArrowPathIcon className={`w-4 h-4 ${resendingEmail[signature.id] ? 'animate-spin' : ''}`} />
                            {resendingEmail[signature.id] ? 'Enviando...' : 'Reenviar Email'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer com Informações Adicionais */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            {/* Deadline Info */}
            {deadline && (
              <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    ⏰ Prazo para assinatura:
                  </span>
                  <span className={`text-sm font-semibold ${deadline.colorClass}`}>
                    {deadline.date}
                    {deadline.days >= 0 && (
                      <span className="text-xs ml-2">({deadline.days === 0 ? 'hoje' : `${deadline.days} ${deadline.days === 1 ? 'dia' : 'dias'}`})</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Botão Fechar */}
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturesModal;
